import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getDriversWithDetails, approveDriver, rejectDriver, createDriver } from '../services/adminDriverService';
import { logActivity } from '../services/adminActivityService';
import AdminAuthService from '../services/adminAuthService';
import { can } from '../utils/adminPermissions';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { DRIVERS_TABLE_ID } from '../lib/appwrite';
import './DriversPage.css';

const STATUS_FILTERS = [
  { id: '', label: 'All' },
  { id: 'pending', label: 'Pending Review' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
];

const getDriverStatus = (d) => {
  if (d.isVerified === true) return 'approved';
  if (d.isVerified === false && d.$createdAt) return 'pending';
  return 'pending';
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, { dateStyle: 'medium' });
};

const DriversPage = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [adminRole, setAdminRole] = useState(null);
  const [docModal, setDocModal] = useState(null); // { docs: [{label,url}], idx: number }
  const [mode, setMode] = useState('view'); // view | create
  const [createForm, setCreateForm] = useState({
    fname: '', lname: '', email: '', password: '', phoneNumber: '',
    nidaNumber: '', vehicleRegistrationNumber: '', vehicleModelName: '',
  });
  const [createError, setCreateError] = useState(null);
  const [creating, setCreating] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [rows, role] = await Promise.all([
        getDriversWithDetails(),
        AdminAuthService.getAdminRole(),
      ]);
      setDrivers(rows || []);
      setAdminRole(role);
    } catch (err) {
      const msg = err.message || 'Failed to load drivers';
      setError(
        msg.toLowerCase().includes('not authorized') || msg.toLowerCase().includes('unauthorized')
          ? `${msg} Add REACT_APP_APPWRITE_API_KEY to .env for server-level access.`
          : msg
      );
      setDrivers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useAutoRefresh(loadData, { realtimeTables: [DRIVERS_TABLE_ID] });

  const canUpdate = adminRole && can(adminRole, 'update', 'drivers');
  const canCreate = adminRole && can(adminRole, 'create', 'drivers');

  const filtered = drivers.filter((d) => {
    const name = [d.fname, d.lname].filter(Boolean).join(' ').toLowerCase();
    const phone = (d.phoneNumber || '').toLowerCase();
    const nida = (d.nidaNumber || '').toLowerCase();
    const vehicle = (d.vehicle?.registrationNumber || d.vehicleNumber || '').toLowerCase();
    const q = search.toLowerCase().trim();
    const matchSearch = !q || name.includes(q) || phone.includes(q) || nida.includes(q) || vehicle.includes(q);
    const status = getDriverStatus(d);
    const matchStatus = !filterStatus || status === filterStatus;
    return matchSearch && matchStatus;
  });

  const pendingCount = drivers.filter((d) => !d.isVerified).length;
  const approvedCount = drivers.filter((d) => d.isVerified === true).length;

  const handleApprove = async (driver) => {
    if (!canUpdate) return;
    setActionLoading(true);
    try {
      await approveDriver(driver.$id);
      await logActivity({
        action: 'approve_driver',
        resourceType: 'driver',
        resourceId: driver.$id,
        details: `Approved driver: ${[driver.fname, driver.lname].filter(Boolean).join(' ')}`,
      });
      await loadData();
      if (selectedDriver?.$id === driver.$id) {
        setSelectedDriver((prev) => prev ? { ...prev, isVerified: true } : prev);
      }
    } catch (err) {
      setError(err.message || 'Failed to approve driver');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (driver) => {
    if (!canUpdate) return;
    if (!window.confirm(`Reject/suspend ${[driver.fname, driver.lname].filter(Boolean).join(' ')}? They will lose access until re-approved.`)) return;
    setActionLoading(true);
    try {
      await rejectDriver(driver.$id);
      await logActivity({
        action: 'reject_driver',
        resourceType: 'driver',
        resourceId: driver.$id,
        details: `Rejected driver: ${[driver.fname, driver.lname].filter(Boolean).join(' ')}`,
      });
      await loadData();
      if (selectedDriver?.$id === driver.$id) {
        setSelectedDriver((prev) => prev ? { ...prev, isVerified: false } : prev);
      }
    } catch (err) {
      setError(err.message || 'Failed to reject driver');
    } finally {
      setActionLoading(false);
    }
  };

  const openCreate = () => {
    setCreateForm({
      fname: '', lname: '', email: '', password: '', phoneNumber: '',
      nidaNumber: '', vehicleRegistrationNumber: '', vehicleModelName: '',
    });
    setCreateError(null);
    setMode('create');
  };

  const closeCreate = () => setMode('view');

  const handleCreateDriver = async () => {
    const fname = createForm.fname.trim();
    const lname = createForm.lname.trim();
    const email = createForm.email.trim();
    const phoneNumber = createForm.phoneNumber.trim();
    const password = createForm.password.trim();
    if (!fname || !lname) { setCreateError('First and last name are required'); return; }
    if (!email) { setCreateError('Email is required'); return; }
    if (!phoneNumber) { setCreateError('Phone number is required'); return; }
    if (password.length < 8) { setCreateError('Password must be at least 8 characters'); return; }

    setCreating(true);
    setCreateError(null);
    try {
      const created = await createDriver({
        fname, lname, email, password, phoneNumber,
        nidaNumber: createForm.nidaNumber.trim(),
        vehicleRegistrationNumber: createForm.vehicleRegistrationNumber,
        vehicleModelName: createForm.vehicleModelName,
      });
      await logActivity({
        action: 'create_driver',
        resourceType: 'driver',
        resourceId: created.$id,
        details: `Added driver (pre-verified, no approval needed): ${fname} ${lname}`,
      });
      await loadData();
      closeCreate();
    } catch (err) {
      setCreateError(err.message || 'Failed to create driver');
    } finally {
      setCreating(false);
    }
  };

  if (loading && drivers.length === 0) {
    return (
      <div className="drivers-loading">
        <div className="drivers-spinner" />
        <p>Loading drivers...</p>
      </div>
    );
  }

  return (
    <div className="drivers-page">
      <div className="drivers-header">
        <div>
          <h1 className="dashboard-title">Drivers</h1>
          <p className="dashboard-subtitle">
            Review driver registrations and manage approvals.
          </p>
        </div>
        <div className="drivers-header-right">
          <div className="drivers-stats">
            <div className="drivers-stat-card drivers-stat-pending">
              <span className="drivers-stat-num">{pendingCount}</span>
              <span className="drivers-stat-label">Pending</span>
            </div>
            <div className="drivers-stat-card drivers-stat-approved">
              <span className="drivers-stat-num">{approvedCount}</span>
              <span className="drivers-stat-label">Approved</span>
            </div>
            <div className="drivers-stat-card">
              <span className="drivers-stat-num">{drivers.length}</span>
              <span className="drivers-stat-label">Total</span>
            </div>
          </div>
          {canCreate && (
            <button type="button" className="drivers-btn-add" onClick={openCreate}>
              + Add Driver
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="drivers-error" role="alert">
          {error}
          <button type="button" onClick={() => setError(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="drivers-toolbar">
        <div className="drivers-search-wrap">
          <img src="/icons/Search.svg" alt="" className="drivers-search-icon" aria-hidden />
          <input
            type="search"
            placeholder="Search by name, phone, NIDA, vehicle..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="drivers-search"
          />
        </div>
        <div className="drivers-filters">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.id}
              className={`drivers-filter-btn${filterStatus === f.id ? ' active' : ''}`}
              onClick={() => setFilterStatus(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="drivers-layout">
        {/* Driver list */}
        <div className="drivers-list-panel">
          {filtered.length === 0 ? (
            <div className="drivers-empty">
              <p>{search || filterStatus ? 'No drivers match your filters.' : 'No drivers registered yet.'}</p>
            </div>
          ) : (
            <div className="drivers-list">
              {filtered.map((d) => {
                const status = getDriverStatus(d);
                const name = [d.fname, d.lname].filter(Boolean).join(' ') || '—';
                const isSelected = selectedDriver?.$id === d.$id;
                return (
                  <div
                    key={d.$id}
                    className={`drivers-list-item${isSelected ? ' selected' : ''}`}
                    onClick={() => setSelectedDriver(d)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && setSelectedDriver(d)}
                  >
                    <div className="drivers-list-avatar">
                      {d.passportPicUrl ? (
                        <img src={d.passportPicUrl} alt={name} className="drivers-avatar-img" />
                      ) : (
                        <span>{(d.fname || '?')[0].toUpperCase()}</span>
                      )}
                    </div>
                    <div className="drivers-list-info">
                      <div className="drivers-list-name">{name}</div>
                      <div className="drivers-list-sub">{d.phoneNumber || '—'}</div>
                      <div className="drivers-list-sub">{d.vehicle?.registrationNumber || d.vehicleNumber || 'No vehicle'}</div>
                    </div>
                    <div className="drivers-list-right">
                      <span className={`drivers-status-badge drivers-status-${status}`}>
                        {status === 'approved' ? 'Approved' : 'Pending'}
                      </span>
                      <div className="drivers-list-date">{formatDate(d.$createdAt)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Detail panel */}
        {selectedDriver ? (
          <div className="drivers-detail-panel">
            <div className="drivers-detail-header">
              <div className="drivers-detail-avatar">
                {selectedDriver.passportPicUrl ? (
                  <img src={selectedDriver.passportPicUrl} alt="Passport" className="drivers-detail-avatar-img" />
                ) : (
                  <span>{(selectedDriver.fname || '?')[0].toUpperCase()}</span>
                )}
              </div>
              <div>
                <h2 className="drivers-detail-name">
                  {[selectedDriver.fname, selectedDriver.lname].filter(Boolean).join(' ') || '—'}
                </h2>
                <span className={`drivers-status-badge drivers-status-${getDriverStatus(selectedDriver)}`}>
                  {selectedDriver.isVerified ? 'Approved' : 'Pending Review'}
                </span>
              </div>
            </div>

            <div className="drivers-detail-section">
              <h3>Personal Details</h3>
              <div className="drivers-detail-grid">
                <div className="drivers-detail-row">
                  <span className="drivers-detail-label">Phone</span>
                  <span className="drivers-detail-val">{selectedDriver.phoneNumber || '—'}</span>
                </div>
                <div className="drivers-detail-row">
                  <span className="drivers-detail-label">NIDA</span>
                  <span className="drivers-detail-val">{selectedDriver.nidaNumber || '—'}</span>
                </div>
                <div className="drivers-detail-row">
                  <span className="drivers-detail-label">Registered</span>
                  <span className="drivers-detail-val">{formatDate(selectedDriver.$createdAt)}</span>
                </div>
              </div>
            </div>

            {selectedDriver.vehicle && (
              <div className="drivers-detail-section">
                <h3>Vehicle</h3>
                <div className="drivers-detail-grid">
                  <div className="drivers-detail-row">
                    <span className="drivers-detail-label">Reg. Number</span>
                    <span className="drivers-detail-val">{selectedDriver.vehicle.registrationNumber || '—'}</span>
                  </div>
                  <div className="drivers-detail-row">
                    <span className="drivers-detail-label">Model</span>
                    <span className="drivers-detail-val">{selectedDriver.vehicle.modelName || '—'}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="drivers-detail-section">
              <h3>Uploaded Documents</h3>
              {(() => {
                const docs = [
                  { label: 'Passport / ID',        url: selectedDriver.passportPicUrl },
                  { label: "Driver's Licence",      url: selectedDriver.licencePicUrl },
                  { label: 'Police Clearance',      url: selectedDriver.policeClearenceUrl },
                  selectedDriver.vehicle?.latraLicenceDocUrl
                    ? { label: 'LATRA Licence',      url: selectedDriver.vehicle.latraLicenceDocUrl }
                    : null,
                  selectedDriver.vehicle?.vehicleRegistrationDocUrl
                    ? { label: 'Vehicle Registration', url: selectedDriver.vehicle.vehicleRegistrationDocUrl }
                    : null,
                ].filter(Boolean);

                return (
                  <div className="drivers-docs-grid">
                    {docs.map((doc, i) => (
                      <DocCard
                        key={doc.label}
                        label={doc.label}
                        url={doc.url}
                        onOpen={doc.url ? () => setDocModal({ docs, idx: i }) : undefined}
                      />
                    ))}
                  </div>
                );
              })()}
            </div>

            {canUpdate && (
              <div className="drivers-detail-actions">
                {!selectedDriver.isVerified ? (
                  <button
                    className="drivers-action-btn drivers-action-approve"
                    onClick={() => handleApprove(selectedDriver)}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Processing…' : '✓ Approve Driver'}
                  </button>
                ) : (
                  <button
                    className="drivers-action-btn drivers-action-reject"
                    onClick={() => handleReject(selectedDriver)}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Processing…' : '✕ Suspend Driver'}
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="drivers-detail-empty">
            <div className="drivers-detail-empty-icon">👤</div>
            <p>Select a driver to view their details and documents.</p>
          </div>
        )}
      </div>

      {docModal && (
        <DocModal
          docs={docModal.docs}
          idx={docModal.idx}
          onClose={() => setDocModal(null)}
          onNav={(newIdx) => setDocModal((prev) => ({ ...prev, idx: newIdx }))}
        />
      )}

      {mode === 'create' && (
        <div className="drivers-modal-overlay" onClick={closeCreate} role="dialog" aria-modal="true">
          <div className="drivers-modal" onClick={(e) => e.stopPropagation()}>
            <div className="drivers-modal-header">
              <h2>Add Driver</h2>
              <button type="button" className="drivers-modal-close" onClick={closeCreate} aria-label="Close">×</button>
            </div>
            <div className="drivers-modal-body">
              <p className="drivers-field-hint drivers-modal-intro">
                Created here, a driver skips the document-review queue — they're marked verified
                immediately and can sign in and go online right away.
              </p>

              {createError && (
                <div className="drivers-error" role="alert">
                  {createError}
                  <button type="button" onClick={() => setCreateError(null)} aria-label="Dismiss">×</button>
                </div>
              )}

              <div className="drivers-create-grid">
                <div className="drivers-modal-field">
                  <label>First name *</label>
                  <input type="text" className="drivers-input" value={createForm.fname}
                    onChange={(e) => setCreateForm((f) => ({ ...f, fname: e.target.value }))} placeholder="Jane" />
                </div>
                <div className="drivers-modal-field">
                  <label>Last name *</label>
                  <input type="text" className="drivers-input" value={createForm.lname}
                    onChange={(e) => setCreateForm((f) => ({ ...f, lname: e.target.value }))} placeholder="Doe" />
                </div>
                <div className="drivers-modal-field drivers-modal-field-wide">
                  <label>Email *</label>
                  <input type="email" className="drivers-input" value={createForm.email}
                    onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))} placeholder="jane@example.com" />
                </div>
                <div className="drivers-modal-field drivers-modal-field-wide">
                  <label>Password *</label>
                  <input type="password" className="drivers-input" value={createForm.password} minLength={8}
                    onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))} placeholder="Min 8 characters" />
                  <span className="drivers-field-hint">Share this password securely — credentials are not emailed</span>
                </div>
                <div className="drivers-modal-field">
                  <label>Phone *</label>
                  <input type="tel" className="drivers-input" value={createForm.phoneNumber}
                    onChange={(e) => setCreateForm((f) => ({ ...f, phoneNumber: e.target.value }))} placeholder="+255..." />
                </div>
                <div className="drivers-modal-field">
                  <label>NIDA number</label>
                  <input type="text" className="drivers-input" value={createForm.nidaNumber}
                    onChange={(e) => setCreateForm((f) => ({ ...f, nidaNumber: e.target.value }))} placeholder="Optional" />
                </div>
                <div className="drivers-modal-field">
                  <label>Vehicle reg. number</label>
                  <input type="text" className="drivers-input" value={createForm.vehicleRegistrationNumber}
                    onChange={(e) => setCreateForm((f) => ({ ...f, vehicleRegistrationNumber: e.target.value }))} placeholder="Optional" />
                </div>
                <div className="drivers-modal-field">
                  <label>Vehicle model</label>
                  <input type="text" className="drivers-input" value={createForm.vehicleModelName}
                    onChange={(e) => setCreateForm((f) => ({ ...f, vehicleModelName: e.target.value }))} placeholder="Optional" />
                </div>
              </div>

              <div className="drivers-modal-actions">
                <button type="button" className="drivers-modal-btn-save" onClick={handleCreateDriver} disabled={creating}>
                  {creating ? 'Adding…' : 'Add Driver'}
                </button>
                <button type="button" className="drivers-btn-cancel" onClick={closeCreate}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Helpers ────────────────────────────────────────────────────────────────

const IMG_EXT = /\.(jpe?g|png|gif|webp|svg|bmp|tiff?|heic|heif|avif|ico)(\?|#|$)/i;
const PDF_EXT = /\.pdf(\?|#|$)/i;

/**
 * Sniff type from URL alone (extension or known endpoint patterns).
 * Returns 'image' | 'pdf' | null (null = unknown, needs HEAD check).
 */
function sniffTypeFromUrl(url) {
  if (!url) return null;
  if (IMG_EXT.test(url)) return 'image';
  if (PDF_EXT.test(url)) return 'pdf';
  // Appwrite /preview endpoint always serves an image (resized)
  if (/\/preview(\?|$)/i.test(url)) return 'image';
  return null;
}

/**
 * Resolve the MIME type of a URL via HEAD request.
 * Returns 'image' | 'pdf' | 'other'.
 */
async function fetchMimeType(url) {
  try {
    const res = await fetch(url, { method: 'HEAD' });
    const ct  = (res.headers.get('content-type') || '').toLowerCase();
    if (ct.startsWith('image/')) return 'image';
    if (ct.includes('pdf'))      return 'pdf';
    return 'other';
  } catch {
    return 'other';
  }
}

// ── DocCard ─────────────────────────────────────────────────────────────────

const DocCard = ({ label, url, onOpen }) => {
  const [thumbMode, setThumbMode] = useState(() => sniffTypeFromUrl(url) ?? 'loading');

  useEffect(() => {
    const guessed = sniffTypeFromUrl(url);
    if (guessed) { setThumbMode(guessed); return; }
    if (!url) { setThumbMode('other'); return; }
    setThumbMode('loading');
    fetchMimeType(url).then(setThumbMode);
  }, [url]);

  const thumb = () => {
    if (!url) return (
      <div className="drivers-doc-missing"><span>📎</span><span>Not uploaded</span></div>
    );
    if (thumbMode === 'loading') return (
      <div className="drivers-doc-missing"><span className="doc-card-spinner" /></div>
    );
    if (thumbMode === 'image') return (
      <img src={url} alt={label} className="drivers-doc-img"
        onError={() => setThumbMode('other')} />
    );
    return (
      <div className="drivers-doc-link">
        <span className="drivers-doc-icon">{thumbMode === 'pdf' ? '📄' : '🔗'}</span>
        <span>{thumbMode === 'pdf' ? 'PDF' : 'Document'}</span>
      </div>
    );
  };

  return (
    <div
      className="drivers-doc-card"
      onClick={onOpen}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onKeyDown={onOpen ? (e) => e.key === 'Enter' && onOpen() : undefined}
      style={{ cursor: onOpen ? 'pointer' : 'default' }}
    >
      <div className="drivers-doc-preview">{thumb()}</div>
      <div className="drivers-doc-label">{label}</div>
    </div>
  );
};

// ── DocModal ────────────────────────────────────────────────────────────────

const DocModal = ({ docs, idx, onClose, onNav }) => {
  const doc      = docs[idx];
  const hasPrev  = idx > 0;
  const hasNext  = idx < docs.length - 1;
  const backdropRef = useRef(null);

  // 'loading' | 'image' | 'pdf' | 'other'
  const [viewMode, setViewMode] = useState('loading');
  // After img fails in the modal, fall back to iframe
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setImgError(false);
    const guessed = sniffTypeFromUrl(doc.url);
    if (guessed) { setViewMode(guessed); return; }
    if (!doc.url) { setViewMode('other'); return; }
    setViewMode('loading');
    fetchMimeType(doc.url).then(setViewMode);
  }, [doc.url]);

  // Keyboard: Esc close, arrows navigate
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape')                onClose();
      if (e.key === 'ArrowLeft'  && hasPrev) onNav(idx - 1);
      if (e.key === 'ArrowRight' && hasNext) onNav(idx + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [idx, hasPrev, hasNext, onClose, onNav]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const renderContent = () => {
    if (!doc.url) return (
      <div className="doc-modal-unsupported">
        <span style={{ fontSize: 48 }}>📎</span>
        <p>This document has not been uploaded yet.</p>
      </div>
    );

    if (viewMode === 'loading') return (
      <div className="doc-modal-unsupported">
        <span className="doc-modal-spinner" />
        <p>Loading preview…</p>
      </div>
    );

    // Image — with automatic fallback to iframe if the browser can't render it
    if (viewMode === 'image' && !imgError) return (
      <img
        src={doc.url}
        alt={doc.label}
        className="doc-modal-img"
        onError={() => setImgError(true)}
      />
    );

    // PDF or image fallback → iframe (browser's native PDF viewer)
    if (viewMode === 'pdf' || imgError) return (
      <iframe
        key={doc.url}
        src={doc.url}
        title={doc.label}
        className="doc-modal-iframe"
      />
    );

    // Unknown type — try iframe first; if that fails user can open externally
    return (
      <iframe
        key={doc.url}
        src={doc.url}
        title={doc.label}
        className="doc-modal-iframe"
      />
    );
  };

  return (
    <div
      className="doc-modal-backdrop"
      ref={backdropRef}
      onClick={(e) => { if (e.target === backdropRef.current) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={`Document: ${doc.label}`}
    >
      <div className="doc-modal-box">
        {/* Header */}
        <div className="doc-modal-header">
          <span className="doc-modal-title">{doc.label}</span>
          <div className="doc-modal-header-actions">
            {doc.url && (
              <a href={doc.url} target="_blank" rel="noopener noreferrer"
                className="doc-modal-ext-btn" title="Open in new tab">
                ↗ Open
              </a>
            )}
            <button className="doc-modal-close" onClick={onClose} aria-label="Close">✕</button>
          </div>
        </div>

        {/* Content */}
        <div className="doc-modal-content">{renderContent()}</div>

        {/* Navigation */}
        {docs.length > 1 && (
          <div className="doc-modal-nav">
            <button className="doc-modal-nav-btn" onClick={() => hasPrev && onNav(idx - 1)}
              disabled={!hasPrev} aria-label="Previous document">← Prev</button>
            <span className="doc-modal-nav-count">{idx + 1} / {docs.length}</span>
            <button className="doc-modal-nav-btn" onClick={() => hasNext && onNav(idx + 1)}
              disabled={!hasNext} aria-label="Next document">Next →</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriversPage;
